import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Navbar, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { filterCategories, filterProvinces, filterSpecies, categoryToListingType } from "@/lib/filterData";
import { X, ImageIcon, Loader2, Trash2 } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { AddressAutocompleteInput } from "@/components/AddressAutocompleteInput";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";

const unitLabels: Record<string, string> = {
  trad: "kr/träd",
  plankor: "kr/planka",
  ved: "kr/kubik",
};

function getListingTypeNormalized(listing: any): string {
  const lt = listing.listing_type?.toLowerCase();
  if (lt === "trad" || lt === "träd") return "trad";
  if (lt === "plankor") return "plankor";
  if (lt === "ved") return "ved";
  const cat = listing.category?.toLowerCase();
  if (cat?.includes("träd")) return "trad";
  if (cat?.includes("plank")) return "plankor";
  if (cat?.includes("ved")) return "ved";
  return "trad";
}

function getProvinceForMunicipality(municipality: string): string {
  for (const p of filterProvinces) {
    if (p.municipalities.includes(municipality)) return p.name;
  }
  return "";
}

export default function EditListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [species, setSpecies] = useState("");
  const [woodAge, setWoodAge] = useState("");
  const [diameter, setDiameter] = useState("");
  const [moistureContent, setMoistureContent] = useState("");
  const [processingGrade, setProcessingGrade] = useState("");
  const [province, setProvince] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [shippingEnabled, setShippingEnabled] = useState(false);
  const [shippingPrice, setShippingPrice] = useState("");
  const [deliveryRadiusKm, setDeliveryRadiusKm] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [plankLength, setPlankLength] = useState("");
  const [plankWidth, setPlankWidth] = useState("");
  const [plankHeight, setPlankHeight] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { data: listing, isLoading } = useQuery({
    queryKey: ["listing-edit", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Redirect non-owner
  useEffect(() => {
    if (listing && user && listing.seller_id !== user.id) {
      navigate(`/annonser/${id}`, { replace: true });
    }
  }, [listing, user]);

  // Populate fields
  useEffect(() => {
    if (!listing || initialized) return;
    setTitle(listing.title || "");
    setDescription(listing.description || "");
    setCategory(listing.category || "");
    setSubCategory((listing as any).sub_category || "");
    setSpecies(listing.species || "");
    setWoodAge(listing.wood_age?.toString() || "");
    setDiameter(listing.diameter || "");
    setMoistureContent(listing.moisture_content || "");
    setProcessingGrade(listing.processing_grade || "");
    setMunicipality(listing.municipality || "");
    setProvince(
      (listing as any).province ||
        (listing.municipality ? getProvinceForMunicipality(listing.municipality) : "")
    );
    setPrice(listing.price?.toString() || "");
    setQuantity(listing.quantity?.toString() || "");
    setPickupEnabled(listing.pickup_enabled ?? true);
    setShippingEnabled(listing.shipping_enabled ?? false);
    setShippingPrice(listing.shipping_price?.toString() || "");
    setDeliveryRadiusKm(listing.delivery_radius_km?.toString() || "");
    setPickupAddress((listing as any).pickup_address || "");
    setPlankLength((listing as any).plank_length_cm?.toString() || "");
    setPlankWidth((listing as any).plank_width_cm?.toString() || "");
    setPlankHeight((listing as any).plank_height_cm?.toString() || "");
    setImages(listing.images || []);
    setInitialized(true);
  }, [listing, initialized]);

  const selectedCategory = filterCategories.find((c) => c.value === category);
  const subOptions = selectedCategory?.subs ?? [];

  const listingType =
    (categoryToListingType[category] ?? (listing ? getListingTypeNormalized(listing) : "trad")) as
      | "trad"
      | "plankor"
      | "ved";
  const unit = unitLabels[listingType] || "kr";
  const priceNum = parseFloat(price) || 0;
  const quantityNum = parseInt(quantity) || 0;
  const total = priceNum * quantityNum;

  const availableMunicipalities = province
    ? filterProvinces.find((p) => p.name === province)?.municipalities || []
    : [];

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !user) return;
    const remaining = 6 - images.length;
    if (remaining <= 0) return;

    setUploading(true);
    const newUrls: string[] = [];
    for (const file of Array.from(files).slice(0, remaining)) {
      try {
        const { blob, ext, contentType } = await compressImage(file);
        const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage.from("listing-images").upload(fileName, blob, { contentType });
        if (error) throw error;
        const { data: urlData } = supabase.storage.from("listing-images").getPublicUrl(fileName);
        newUrls.push(urlData.publicUrl);
      } catch (err: any) {
        toast.error("Kunde inte ladda upp bild: " + err.message);
      }
    }
    setImages((prev) => [...prev, ...newUrls]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const pickupAddressMissing = pickupEnabled && !pickupAddress.trim();

  const handleSave = async () => {
    if (!listing || !user) return;
    if (!title.trim()) { toast.error("Titel krävs"); return; }
    if (priceNum <= 0) { toast.error("Ange ett giltigt pris"); return; }
    if (!pickupEnabled && !shippingEnabled) { toast.error("Minst ett leveransalternativ krävs"); return; }
    if (pickupAddressMissing) { toast.error("Ange en upphämtningsadress."); return; }

    setSaving(true);
    const deliveryValue = pickupEnabled && shippingEnabled ? "båda" : shippingEnabled ? "leverans" : "hämta";

    const { error } = await supabase
      .from("listings")
      .update({
        title,
        description,
        category: category || listing.category,
        sub_category: subCategory || null,
        species,
        listing_type: listingType,
        wood_age: woodAge ? parseInt(woodAge) : null,
        diameter: diameter || null,
        moisture_content: moistureContent || null,
        processing_grade: processingGrade || null,
        province: province || null,
        municipality: municipality || null,
        location: municipality || null,
        price: priceNum,
        quantity: quantityNum || null,
        pickup_enabled: pickupEnabled,
        shipping_enabled: shippingEnabled,
        shipping_price: shippingPrice ? parseFloat(shippingPrice) : null,
        delivery_radius_km: deliveryRadiusKm ? parseFloat(deliveryRadiusKm) : null,
        pickup_address: pickupEnabled ? pickupAddress.trim() : null,
        plank_length_cm: listingType === "plankor" && plankLength ? parseFloat(plankLength) : null,
        plank_width_cm: listingType === "plankor" && plankWidth ? parseFloat(plankWidth) : null,
        plank_height_cm: listingType === "plankor" && plankHeight ? parseFloat(plankHeight) : null,
        delivery: deliveryValue,
        images,
        updated_at: new Date().toISOString(),
      } as any)
      .eq("id", listing.id);

    setSaving(false);
    if (error) {
      toast.error("Kunde inte spara: " + error.message);
    } else {
      toast.success("Dina ändringar har sparats.");
    }
  };

  const handleDelete = async () => {
    if (!listing) return;
    setDeleting(true);
    const { error } = await supabase.from("listings").delete().eq("id", listing.id);
    setDeleting(false);
    if (error) {
      toast.error("Kunde inte ta bort: " + error.message);
    } else {
      toast.success("Annonsen har tagits bort.");
      navigate("/dashboard");
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="container flex flex-1 items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <Footer hideOnMobile />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="container flex flex-1 items-center justify-center py-20">
          <p className="text-lg font-medium">Annonsen hittades inte</p>
        </div>
        <Footer hideOnMobile />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <div className="container max-w-2xl flex-1 py-8">
        <BackButton fallback="/dashboard" />

        <h1 className="mb-8 font-display text-2xl font-bold text-foreground">Redigera annons</h1>

        {/* Section 1 – Bilder */}
        <section className="mb-2">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Bilder</h2>

          {images.length > 0 && (
            <div className="mb-4 flex gap-3 overflow-x-auto pb-2 my-0 py-[10px]">
              {images.map((url, i) => (
                <div key={i} className="relative shrink-0">
                  <img src={url} alt={`Bild ${i + 1}`} className="h-24 w-24 rounded-lg object-cover" />
                  <button
                    onClick={() => removeImage(i)}
                    className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {images.length >= 6 ? (
            <p className="text-sm text-muted-foreground">Du har uppnått maxgränsen på 6 bilder.</p>
          ) : (
            <>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ImageIcon className="mr-2 h-4 w-4" />
                )}
                Lägg till bilder
              </Button>
            </>
          )}
        </section>

        <Separator className="my-6" />

        {/* Section 2 – Grundinformation */}
        <section className="mb-2">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Grundinformation</h2>
          <div className="space-y-4">
            <div>
              <Label>Titel</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Annonstitel" />
            </div>
            <div>
              <Label>Beskrivning</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Beskriv din annons..." rows={4} />
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        {/* Section 3 – Kategori */}
        <section className="mb-2">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Kategori</h2>
          <div className="space-y-4">
            <div>
              <Label>Kategori</Label>
              <Select value={category} onValueChange={(v) => { setCategory(v); setSubCategory(""); }}>
                <SelectTrigger><SelectValue placeholder="Välj kategori" /></SelectTrigger>
                <SelectContent>
                  {filterCategories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {subOptions.length > 0 && (
              <div>
                <Label>Underkategori <span className="text-muted-foreground font-normal">(valfritt)</span></Label>
                <Select value={subCategory || "__none__"} onValueChange={(v) => setSubCategory(v === "__none__" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Välj underkategori" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Ingen</SelectItem>
                    {subOptions.slice().sort((a, b) => a.localeCompare(b, "sv")).map((sub) => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </section>

        <Separator className="my-6" />

        {/* Section 4 – Trädslag */}
        <section className="mb-2">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Trädslag</h2>
          <Select value={species} onValueChange={setSpecies}>
            <SelectTrigger><SelectValue placeholder="Välj trädslag" /></SelectTrigger>
            <SelectContent>
              {filterSpecies.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </section>

        <Separator className="my-6" />

        {/* Section 4 – Träinformation */}
        <section className="mb-2">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Träinformation</h2>
          <div className="space-y-4">
            <div>
              <Label>Ålder (år)</Label>
              <Input type="number" value={woodAge} onChange={(e) => setWoodAge(e.target.value)} placeholder="T.ex. 30" />
            </div>

            {listingType === "trad" && (
              <div>
                <Label>Genomsnittlig diameter (cm)</Label>
                <Input value={diameter} onChange={(e) => setDiameter(e.target.value)} placeholder="T.ex. 25" />
              </div>
            )}

            {(listingType === "plankor" || listingType === "ved") && (
              <div>
                <Label>Fuktkvot</Label>
                <Select value={moistureContent} onValueChange={setMoistureContent}>
                  <SelectTrigger><SelectValue placeholder="Välj fuktkvot" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="< 16-18%">{"< 16-18%"}</SelectItem>
                    <SelectItem value="> 20%">{"> 20%"}</SelectItem>
                    <SelectItem value="> 24-30%">{"> 24-30%"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {listingType === "plankor" && (
              <div>
                <Label>Bearbetningsgrad</Label>
                <Select value={processingGrade} onValueChange={setProcessingGrade}>
                  <SelectTrigger><SelectValue placeholder="Välj bearbetningsgrad" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Hyvlat">Hyvlat</SelectItem>
                    <SelectItem value="Råsågat">Råsågat</SelectItem>
                    <SelectItem value="Kantat">Kantat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {listingType === "plankor" && (
              <div>
                <Label>Dimensioner (cm)</Label>
                <div className="mt-1.5 grid grid-cols-3 gap-2">
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={plankLength}
                    onChange={(e) => setPlankLength(e.target.value)}
                    placeholder="Längd"
                  />
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={plankWidth}
                    onChange={(e) => setPlankWidth(e.target.value)}
                    placeholder="Bredd"
                  />
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={plankHeight}
                    onChange={(e) => setPlankHeight(e.target.value)}
                    placeholder="Höjd"
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        <Separator className="my-6" />

        {/* Section 5 – Plats */}
        <section className="mb-2">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Plats</h2>
          <div className="space-y-4">
            <div>
              <Label>Landskap</Label>
              <Select value={province} onValueChange={(v) => { setProvince(v); setMunicipality(""); }}>
                <SelectTrigger><SelectValue placeholder="Välj landskap" /></SelectTrigger>
                <SelectContent>
                  {filterProvinces.map((p) => (
                    <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {province && (
              <div>
                <Label>Kommun</Label>
                <Select value={municipality} onValueChange={setMunicipality}>
                  <SelectTrigger><SelectValue placeholder="Välj kommun" /></SelectTrigger>
                  <SelectContent>
                    {availableMunicipalities.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </section>

        <Separator className="my-6" />

        {/* Section 6 – Pris & Kvantitet */}
        <section className="mb-2">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Pris & Kvantitet</h2>
          <div className="space-y-4">
            <div>
              <Label>Pris ({unit})</Label>
              <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
            </div>
            <div>
              <Label>Antal / Mängd</Label>
              <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" />
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-border bg-card p-4 text-center">
            <p className="text-sm text-muted-foreground">Totalt värde</p>
            <p className="text-3xl font-bold text-foreground">{total.toLocaleString("sv-SE")} kr</p>
            {priceNum > 0 && (
              <p className="mt-1 text-sm text-muted-foreground">{priceNum.toLocaleString("sv-SE")} {unit}</p>
            )}
          </div>
        </section>

        <Separator className="my-6" />

        {/* Section 7 – Leverans */}
        <section className="mb-2">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Leverans</h2>
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Upphämtning</p>
                  <p className="text-xs text-muted-foreground">Köparen hämtar hos dig</p>
                </div>
                <Switch checked={pickupEnabled} onCheckedChange={setPickupEnabled} />
              </div>
              {pickupEnabled && (
                <div className="mt-4 space-y-2 border-t border-border pt-4">
                  <Label htmlFor="edit-pickup-address">Upphämtningsadress</Label>
                  <AddressAutocompleteInput
                    id="edit-pickup-address"
                    value={pickupAddress}
                    onChange={setPickupAddress}
                    placeholder="Ange adressen där virket hämtas"
                  />
                  {pickupAddressMissing && (
                    <p className="text-sm text-destructive">Ange en upphämtningsadress.</p>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Frakt</p>
                  <p className="text-xs text-muted-foreground">Du levererar till köparen</p>
                </div>
                <Switch checked={shippingEnabled} onCheckedChange={setShippingEnabled} />
              </div>

              {shippingEnabled && (
                <div className="mt-4 space-y-3 border-t border-border pt-4">
                  <div>
                    <Label>Fraktpris (kr)</Label>
                    <Input type="number" value={shippingPrice} onChange={(e) => setShippingPrice(e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    <Label>Leveransradius (km)</Label>
                    <Input type="number" value={deliveryRadiusKm} onChange={(e) => setDeliveryRadiusKm(e.target.value)} placeholder="T.ex. 50" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {!pickupEnabled && !shippingEnabled && (
            <p className="mt-2 text-sm text-destructive">Minst ett leveransalternativ måste vara valt.</p>
          )}
        </section>

        <Separator className="my-6" />

        {/* Save & Delete */}
        <div className="space-y-3 pb-8">
          <Button
            className="w-full bg-primary hover:bg-primary/90"
            size="lg"
            disabled={saving || pickupAddressMissing}
            onClick={handleSave}
          >
            {saving ? "Sparar…" : "Spara ändringar"}
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="w-full border-destructive text-destructive hover:bg-destructive/10" size="lg">
                <Trash2 className="mr-2 h-4 w-4" /> Ta bort annons
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Ta bort annons</AlertDialogTitle>
                <AlertDialogDescription>
                  Är du säker på att du vill ta bort den här annonsen? Detta går inte att ångra.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={deleting}
                >
                  {deleting ? "Tar bort…" : "Ta bort"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <Footer hideOnMobile />
    </div>
  );
}
